<template>
  <div class="dashboard">
    <breadcrumbs :items="breadcrumbs">
      <div class="report-filters">
        <div class="download-button" v-if="showDownload">
          <rp-button icon="get_app" @click="onClickDownload">
            Download Data
          </rp-button>
          <div class="dropdown-area" v-if="showDropdown" v-click-outside="onClickDownload">
            <a v-for="item, index in downloadOptions" :key="index" @click="downloadReport(index)">
              {{item}}
            </a>
          </div>
        </div>
        <div v-show="isHospitalAdmin" class="filter">
          <rp-dropdown
            v-model="hospitalAdminSurgeonSelected"
            :data-model="hospitalAdminSurgeons"
            objectLabel="full_name"
            spriteName="surgeon"
            scrollable
            tempIdentifier="Surgeon"
            placeholder="Surgeon"
            @input="hospitalAdminSurgeonChanged"
          />
        </div>
        <div 
          v-if="isAdmin"
          class="filter">
          <rp-dropdown
            v-if="isAdmin"
            v-model="countriesAdminSelected"
            :data-model="adminCountries"
            objectLabel="name"
            spriteName="language"
            scrollable
            multiselect
            tempIdentifier="Country"
            placeholder="Country"
            @input="countryFilter"
          />
        </div>

        <div 
          v-if="isDistributor"
          class="filter">
          <rp-dropdown
            v-if="isDistributor"
            v-model="countriesSelected"
            :data-model="distributorCountries"
            objectLabel="name"
            spriteName="language"
            scrollable
            multiselect
            tempIdentifier="Country"
            placeholder="Country"
            @input="countryFilter"
          />
        </div>

        <div class="filter">
          <rp-dropdown
            v-model="iolModelsSelected"
            :data-model="iolModels" 
            objectLabel="model"
            spriteName="lens"
            scrollable
            multiselect
            tempIdentifier="Iol"
            placeholder="Product"
            @input="getReports"

          />
        </div>

        <div class="filter">
          <rp-dropdown
            v-model="hospitalSelected"
            :data-model="hospitals" 
            objectLabel="label"
            spriteName="filter_list"
            scrollable
            multiselect
            tempIdentifier="Hospital"
            placeholder="Hospital"
            @input="getReports"
          />
        </div>

        <div class="filter">
            <rp-dropdown
            v-model="dateSelected"
            :data-model="dates"
            objectLabel="name"
            spriteName="date_range"
            scrollable
            tempIdentifier="Time"
            placeholder="All time"
            @input="getReports"
          />
        </div>
      </div>
    </breadcrumbs>

    <div class="content">
      <div :class="['router-view', isNoActionBar() ? 'dashboard-no-action' : '']">
        <router-view
          ref="current_breakdown"
          :iols="iolModels"
          :pagedReports="pagedReports"
          :globalReports="globalReports"
          :contextSurgeon="hospitalAdminSurgeonSelected"
          @addIolFilter="addIolFilter"
          @removeIolFilter="removeIolFilter"
          @setDownloadOptions="setDownloadOptions"
        />
      </div>
    </div>

  </div>
</template>

<script>
/* eslint-disable max-lines */

import Multiselect from 'raypro_ui/src/components/ui/inputs/Multiselect'
import Dropdown from 'raypro_ui/src/components/ui/inputs/dropdown/Dropdown'
import RpButton from 'raypro_ui/src/components/ui/inputs/Button'
import Logo from 'raypro_ui/src/components/ui/Logo'
import MyRankings from './MyRankings'
import Breadcrumbs from '@/components/Breadcrumbs'

import Select from 'raypro_ui/src/components/ui/inputs/StyledSelect'
import Sprite from 'raypro_ui/src/components/ui/Sprite'
import page from '@/mixins/page'
import Permissions from 'raypro_ui/src/components/Permissions'
import ClickOutside from 'raypro_ui/src/directives/clickOutside'
import * as websiteTypes from 'raypro_ui/src/store/modules/website/types'

import {
  getSurgeonReports,
  getSurgeonScore,
  getAdminReports,
  getDistributorReports,
  getHospitalAdminReports,
  getGlobalReports
} from 'raypro_ui/src/api/reports'
import {getIolModels} from 'raypro_ui/src/api/iols'
import {getUserHospitals, getUserHospitalsByCountries, getAssociatedSurgeonsForHospitalAdmin} from 'raypro_ui/src/api/users'
import {getHospitalsWithoutPagination} from 'raypro_ui/src/api/hospitals'
import {mapGetters, mapMutations} from 'vuex'

export default {
  mixins: [page],
  directives: {ClickOutside},

  components: {
    MyRankings,
    Breadcrumbs,
    'rp-logo': Logo,
    'rp-button': RpButton,
    'rp-permissions': Permissions,
    'rp-select': Select,
    'rp-sprite': Sprite,
    'rp-multiselect': Multiselect,
    'rp-dropdown': Dropdown
  },

  data() {
    return {
      multiselectOptions: [],
      reports: [],
      pagedReports: [],
      globalReports: [],
      downloadOptions: [],
      showDownload: false,
      showDropdown: false,
      showRankings: false,
      ranking: {},
      title: this.$route.name,
      iolModelsSelected: [],
      iolModels: [],
      hospitalSelected: [],
      hospitals: [],
      countriesSelected: [],
      dateSelected: '',
      adminCountries: [],
      countriesAdminSelected: [],
      hospitalAdminSurgeons: [],
      hospitalAdminSurgeonSelected: {},
      adminSurgeonMappings: [],
      currentContextId: '',
      dates: [
        {
          name: 'All time',
          value: '',
          id: 0
        },
        {
          name: 'Last month',
          value: '30',
          id: 1
        },
        {
          name: 'Last 3 months',
          value: '90',
          id: 2
        },
        {
          name: 'Last 6 months',
          value: '180',
          id: 3
        },
        {
          name: 'Last 12 months',
          value: '365',
          id: 4
        }
      ],
      reportsSchema: {
        allPages: [
          'satisfaction_with_surgeon', 'satisfaction_with_outcomes', 'satisfaction_with_hospital', 'target_refraction',
          'dysphotopsia', 'spectacle_independence', 'iol_usage', 'catprom_preop', 'catprom', 'catquest_preop', 'catquest', 
          'registered_patients', 'registered_users', '%PCO', '%Supplementary Lens', '%Cornea Treatment', 'quality_of_service',
          'clinic_recommendation', 'doctor_confidence'
        ]
      }
    }
  },

  async created() {
    this.getUserHospitals()
    this.getIolModels()

    if (this.isHospitalAdmin) {
      this.currentContextId = this.authUser.id
      await this.getSurgeonsForHospitalAdmin()
      await this.getAdminSurgeonMappings()
    }
  },

  mounted() {
    if(this.canReadReports) {
      this.getReports()
      this.getGlobalReports()
    }
  },

  watch: {
    '$route.name'(newVal) {
      this.title = newVal
      if(newVal === 'Overview') {
        if(this.canReadReports) {
          this.getReports()
        }
      }
      this.showDownload = false
      this.downloadOptions = []
    }
  },

  computed: {
    ...mapGetters(['authUser', 'isAdmin', 'isDistributor', 'isHospitalAdmin', 'distributorCountries']),

    reportUser() {
      if (this.isHospitalAdmin && this.contextSurgeonValid) {
        return this.hospitalAdminSurgeonSelected
      }

      return this.authUser
    },

    contextSurgeonValid() {
      return (this.hospitalAdminSurgeonSelected &&
        Object.keys(this.hospitalAdminSurgeonSelected).includes('id'))
    },

    canReadReports() {
      const roles = this.authUser.roles[0].permissions.default

      return roles.includes('admin_read_reports') || roles.includes('read_reports')
    },

    canReadScore() {
      return this.authUser.roles[0].permissions.default.includes('read_raypro_score')
    },

    currentBreadcrumb() {
      return this.$route.name
    },

    breadcrumbs() {
      return [
        {
          label: 'Dashboard',
          link: 'Overview'
        },
        {label: this.currentBreadcrumb}
      ]
    },
    filterQuery() {
      let filterStr = ''

      if(this.iolModelsSelected && this.iolModelsSelected.length > 0) {
        const filterAdjust = this.getDataForQury(this.iolModelsSelected, 'model')

        filterStr += `iol=${filterAdjust.join(',')}&`
      }

      if(this.hospitalSelected) {
        const filterAdjust = this.getDataForQury(this.hospitalSelected, 'value')

        if(filterAdjust.length > 0) {
          filterStr += `hospitals=${filterAdjust.join(',')}&`
        }
      }

      if(this.dateSelected) {
        const filterAdjust = this.getDataForQury(this.dateSelected, 'value')

        if(filterAdjust.length > 0) {
          filterStr += `time_period=${filterAdjust}&`
        }
      }

      if(this.isAdmin) {
        if(this.countriesAdminSelected && this.countriesAdminSelected.length > 0) {
          const filterAdjust = this.getDataForQury(this.countriesAdminSelected, 'id')

          filterStr += `countries=${filterAdjust.join(',')}&`
        }
      } else if(this.isDistributor) {
        if(this.countriesSelected && this.countriesSelected.length > 0) {
          const filterAdjust = this.getDataForQury(this.countriesSelected, 'id')

          filterStr += `countries=${filterAdjust.join(',')}&`
        }
      }

      if(filterStr.length > 1) {
        filterStr = `?${filterStr}`
      }

      return filterStr.slice(0, -1)
    }
  },

  methods: {
    ...mapMutations([websiteTypes.SIDENAV_LINKS]),

    getDataForQury(item, attribute) {
      let result

      if(Array.isArray(item)) {
        result = []
        item.forEach((element) => {
          result.push(element[attribute])
        })
      } else {
        result = item[attribute]
      }
      return result
    },

    countryFilter() {
      this.getUserHospitals()
      this.hospitalSelected = []
      this.getReports()
    },

    getReports() {
      const filters = this.filterQuery
      const payload = {
        id: this.reportUser.id,
        filters
      }

      if(this.$route.name === 'Overview') {
        if(this.canReadScore) {
          getSurgeonScore(payload).then(({data}) => {
            this.ranking = data
          })
        }

        let reportsPromise

        if(this.isAdmin) {
          reportsPromise = (payload) => { return getAdminReports(payload) }
        } else if(this.isDistributor) {
          reportsPromise = (payload) => { return getDistributorReports(payload) }
        } else if(this.isHospitalAdmin && !this.contextSurgeonValid) {
          reportsPromise = (payload) => { return getHospitalAdminReports(payload) }
        } else {
          reportsPromise = (payload) => { return getSurgeonReports(payload) }
        }

        reportsPromise(payload).then(({data}) => {
          this.reports = data
          // Modify report names/data as necessary
          const reports = data.map(el => {
            if (el.hasOwnProperty('name')) {
              if (el.name === 'Target refraction') {
                el.name = 'Expectations met'
                delete el.report_datas.data.not_sure
              } else if (el.name === 'Dysphotopsia') {
                el.name = 'Visual disturbances'
              }
            }
            return el
          })

          // Update sidebar navigation links
          const copy = JSON.parse(JSON.stringify(reports))
          const sidenavLinks = copy.filter((e, i) => {
            return copy.findIndex((a) => {
              return a.slug === e.slug && a.slug !== 'additional_procedures'
            }) === i
          })
          this[websiteTypes.SIDENAV_LINKS](sidenavLinks)

          // Split reports into pages and push into pagedReports
          this.pagedReports = []
          const processedReports = this.processReports(reports)
          while (processedReports.length > 0) {
            this.pagedReports.push(processedReports.splice(0, 7))
          }
        })
      }
    },

    getGlobalReports() {
      getGlobalReports().then(({data}) => {
        this.globalReports = data
      })
    },

    preProcessData(data) {
      let newData = JSON.parse(JSON.stringify(data))
      newData = newData.map((report) => {
        if (report.slug === 'additional_procedures') {
          report.slug = `%${report.category}`
          report.name = report.category
          report.implementation_module = 'MultipleAnswers'
          report.category = null
        }
        return report
      })

      return newData
    },

    processReports(data) {
      data = this.preProcessData(data)
      const nested = {satisfaction_with_outcomes: [], spectacle_independence: [], dysphotopsia:[]}

      const filteredArray = data.filter((report) => {
        if (Object.keys(nested).includes(report.slug)) {
          nested[report.slug].push(report)
        } else {
          return true
        }
      })

      let returnArray = []
      for (var slug of this.reportsSchema.allPages) {
        if (nested.hasOwnProperty(slug)) {
          returnArray.push(nested[slug])
        } else if (filteredArray.find((x) => x.slug === slug)) {
          returnArray.push(filteredArray.find((x) => x.slug === slug))
        }
      }

      return returnArray
    },

    getCountriesPayload() {
      if(this.countriesSelected.length > 0) {
        const payload = `?countries=${ this.getDataForQury(this.countriesSelected, 'id').join(',')}`

        return payload
      }
      return ''
    },

    getUserHospitals() {
      let getHospitalsFunction
      const id = this.authUser.id

      if(this.isAdmin) {
        getHospitalsFunction = () => { return getHospitalsWithoutPagination() }
      } else if(this.isDistributor) {
        const countriesPayload = this.getCountriesPayload()

        getHospitalsFunction = () => {
          return getUserHospitalsByCountries({id}, countriesPayload)
        }
      } else {
        getHospitalsFunction = () => { return getUserHospitals({id}) }
      }
      getHospitalsFunction().then((res) => {
        const response = res.data

        const blob = response.map((h) => {
          return {
            label: h.name,
            value: h.id
          }
        })

        this.hospitals = blob

        if(this.isAdmin && this.adminCountries.length === 0) {
          const countriesDict = {}

          for(const hospital of response) {
            if(!countriesDict[hospital.country.id]) {
              countriesDict[hospital.country.id] = hospital.country
            }
          }

          for(const key in countriesDict) {
            this.adminCountries.push(countriesDict[key])
          }
        }
      })
    },

    getIolModels() {
      getIolModels()
        .then(({data}) => {
          this.iolModels = data
          this.multiselectOptions = data.map((el) => { return el.model })
        })
        .catch(() => {
        })
    },

    async getSurgeonsForHospitalAdmin() {
      const data = await getAssociatedSurgeonsForHospitalAdmin()
      this.hospitalAdminSurgeons = data.data.map((el) => {
        el["full_name"] = `${el.profile.salutation} ${el.profile.first_name} ${el.profile.last_name}`.trim()
        return el
      }).sort((a, b) => {
        if (a.full_name < b.full_name) {
          return -1
        } else if (a.full_name > b.full_name) {
          return 1
        } else {
          return 0
        }
      })

      this.hospitalAdminSurgeons.unshift({full_name: 'All Surgeons'})
    },

    async getAdminSurgeonMappings() {
      this.adminSurgeonMappings = []
      if (this.hospitalAdminSurgeons.length > 1) {
        const surgeon = this.hospitalAdminSurgeons[1]
        const data = await getSurgeonReports({id: surgeon.id})
        this.reports.forEach((report) => {
          var surgeonReport = data.data.find((x) => x.slug === report.slug && x.category === report.category)
          if (surgeonReport) {
            this.adminSurgeonMappings.push({
              slug: report.slug,
              category: report.category,
              admin_report_id: report.id,
              surgeon_report_id: surgeonReport.id
            })
          }
        })
      }
    },

    hospitalAdminSurgeonChanged() {
      if (this.$route.name !== 'Overview') {
        if ((this.$route.params.report_id) && (
            (this.contextSurgeonValid && this.currentContextId === this.authUser.id) ||
            (this.contextSurgeonValid === false && this.currentContextId !== this.authUser.id))) {

          // Switch context and route to other report
          const mapping = this.adminSurgeonMappings.find((x) => {
            return x.admin_report_id === this.$route.params.report_id ||
            x.surgeon_report_id === this.$route.params.report_id
          })

          if (mapping) {
            const otherId = mapping.admin_report_id === this.$route.params.report_id ? mapping.surgeon_report_id : mapping.admin_report_id
            this.$router.push({name: this.$route.name, params: {'report_id': otherId}})
          }
        }
      }

      this.currentContextId = this.reportUser.id
      this.getReports()
    },

    openMyRankings() {
      this.showRankings = true
    },

    closeMyRankings() {
      this.showRankings = false
    },

    isNoActionBar() {
      return this.$route.path.startsWith("/dashboard")
    },

    setDownloadOptions(options) {
      this.showDownload = !!options
      this.downloadOptions = options
    },

    onClickDownload() {
      if (this.downloadOptions.length > 1) {
        this.showDropdown = !this.showDropdown
      } else {
        this.downloadReport(0)
      }
    },

    downloadReport(index) {
      this.showDropdown = false
      if (this.$refs['current_breakdown'] !== undefined &&
          typeof this.$refs['current_breakdown'].downloadReport === 'function') {
        this.$refs['current_breakdown'].downloadReport(index)
      }
    },

    addIolFilter(iolId) {
      const iol = this.iolModels.find((x) => { return x.id === iolId })
      if (iol && !this.iolModelsSelected.includes(iol)) {
        this.iolModelsSelected.push(iol)
      }
    },

    removeIolFilter(iolId) {
      const iol = this.iolModels.find((x) => { return x.id === iolId })
      if (iol && this.iolModelsSelected.includes(iol)) {
        this.iolModelsSelected.splice(this.iolModelsSelected.indexOf(iol), 1)
      }
    },
  }
}
</script>

<style lang="sass">
  @import 'raypro_ui/src/styles/base/_variables.sass'
  @import 'raypro_ui/src/styles/helpers/_mixins.sass'
  @import '../../../stylesheets/helpers/extends'
  @import 'vue-multiselect/dist/vue-multiselect.min.css'
  
  .multiselect
    min-height: 30px
    width: 150px
    font-size: 10px
    line-height: 12px

  .breadcrumbs
    flex-wrap: wrap
    ul
      align-self: flex-start

  .report-filters
    display: flex
    //flex-direction: column
    justify-content: center

    @media screen and (min-width: 1200px)
      flex-direction: row
      align-items: flex-start

    .download-button
      margin-top: -4px
      min-width: 120px

      .btn
        padding: 10px
        text-transform: none
        color: $color-grey
        border: 0px

        .sprite
          order: -1
          width: 20px
          height: 20px
          margin-left: 0px
          fill: $color-grey

      .dropdown-area
        position: absolute
        display: flex
        flex-direction: column
        gap: 8px
        padding: 10px
        font-size: 12px
        text-align: left
        border: 1px solid $color-grey
        background: $color-panel-bg
        z-index: 1

        a
          color: $color-text

    .filter
      padding: 15px 0 0
      min-width: 200px
      margin-left: 10px

      @include media($media-small)
        width: 100%
        margin-bottom: 15px
        min-width: 150px

      .dropdown-wrapper
        border-radius: 5px
        background-color: #FFF

        .input-icon
          margin: 5px 5px 0 5px

        .dropdown-wrap
          margin-right: 5px

          .dropdown__relative-wrapper
            left: -20px
            width: calc(100% + 20px)

  .slide-left-enter-active, .slide-left-leave-active
    transition: transform .5s
    transform: translateX(0%)

  .slide-left-enter, .slide-left-leave-to
    transform: translateX(100%)

  .ranking
    display: flex
    flex-direction: column

    .top-part
      display: flex
      flex-direction: column
    
  .panel-icon
    fill: #5B6C72
    max-height: 60px
    max-width: 60px

  .score
    font-size: 50px
    color: $color-side-nav
    margin-right: 10px

    .percent
      font-size: 20px
      
</style>



// WEBPACK FOOTER //
// src/components/pages/Dashboard/Dashboard_v2.vue